// src/services/driverService.js

// Import Firebase Firestore functions
import { db } from "../firebase/config";
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  doc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";

/**
 * Driver Service
 *
 * Manages the `drivers` collection in Firestore. Each document is keyed by
 * the driver's Firebase Auth UID (NOT auto-generated), so the document path
 * `drivers/{uid}` is what Firestore Security Rules check against
 * `request.auth.uid` to decide whether the signed-in user is a driver.
 *
 * Collection: `drivers`
 * Doc ID:     Firebase Auth UID (manually entered by admin when adding)
 *
 * Document shape (see `validFields` allowlist in `sanitizeDriverData`):
 * - name          string  — display name
 * - phone         string  — contact phone (free-text, no validation)
 * - email         string  — mirrors the Auth user's email (display-only,
 *                            source of truth is Firebase Auth)
 * - licensePlate  string? — optional vehicle reg
 * - notes         string? — free-text admin notes
 * - disabled      boolean — true = locked out (Auth user must also be
 *                            disabled in Firebase Console for full lockout)
 * - createdAt     Timestamp
 * - updatedAt     Timestamp
 *
 * ⚠️ ALLOWLIST GOTCHA — any field not in `validFields` below is silently
 * stripped before writeback. When adding a new schema field, register it
 * here FIRST. See `_framework/decisions.md` 2026-05-13 entry
 * "Calendar manual-sort pattern + sanitizer-allowlist lesson" — same
 * lesson applies to every service in this repo.
 */

// ==========================================
// COLLECTION REFERENCE
// ==========================================

const DRIVERS_COLLECTION = "drivers";

const getDriversCollection = () => collection(db, DRIVERS_COLLECTION);

// ==========================================
// VALIDATION + SANITISATION
// ==========================================

/**
 * Validate driver data before write. Allow partial validation for updates.
 */
const validateDriverData = (driver, isUpdate = false) => {
  const requiredFields = ["name", "email"];

  if (!isUpdate) {
    for (const field of requiredFields) {
      if (!driver[field] || !driver[field].toString().trim()) {
        return {
          isValid: false,
          error: `Missing required field: ${field}`,
        };
      }
    }
  }

  if (driver.name && driver.name.length > 120) {
    return {
      isValid: false,
      error: "Name must be 120 characters or less",
    };
  }

  // Light email shape check — Firebase Auth is the real validator
  if (driver.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(driver.email)) {
    return {
      isValid: false,
      error: "Email looks invalid",
    };
  }

  return { isValid: true, error: null };
};

/**
 * Sanitise driver data. Strips any field not in the allowlist below.
 *
 * @see header docstring for field reference
 */
const sanitizeDriverData = (driver) => {
  const sanitized = {};

  const validFields = [
    "name",
    "phone",
    "email",
    "licensePlate",
    "notes",
    "disabled",
  ];

  validFields.forEach((field) => {
    if (driver[field] !== undefined && driver[field] !== null) {
      if (typeof driver[field] === "string") {
        sanitized[field] = driver[field].trim();
      } else {
        sanitized[field] = driver[field];
      }
    }
  });

  sanitized.updatedAt = Timestamp.now();

  return sanitized;
};

// ==========================================
// CRUD
// ==========================================

/**
 * Get all drivers, ordered by name. Optional `includeDisabled` flag — by
 * default disabled drivers are EXCLUDED, since the most common caller is
 * the assignment dropdown which shouldn't see them.
 *
 * @param {Object}  [options]
 * @param {boolean} [options.includeDisabled=false]
 * @returns {Promise<Array>} drivers (each with id = uid)
 */
const getDrivers = async ({ includeDisabled = false } = {}) => {
  try {
    const driversRef = getDriversCollection();
    const driversQuery = query(driversRef, orderBy("name", "asc"));
    const snapshot = await getDocs(driversQuery);
    const drivers = [];
    snapshot.forEach((d) => {
      const data = d.data();
      if (!includeDisabled && data.disabled === true) return;
      drivers.push({ id: d.id, ...data });
    });
    return drivers;
  } catch (error) {
    console.error("❌ Error fetching drivers:", error);
    throw new Error(`Failed to fetch drivers: ${error.message}`);
  }
};

/**
 * Get a single driver by Firebase Auth UID.
 * Returns null if no `drivers/{uid}` doc exists (caller can treat that as
 * "not a driver" — useful in the driver-portal AuthGuard).
 */
const getDriverByUid = async (uid) => {
  try {
    if (!uid || typeof uid !== "string") {
      throw new Error("Driver UID is required and must be a string");
    }
    const docRef = doc(db, DRIVERS_COLLECTION, uid);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    console.error(`❌ Error fetching driver ${uid}:`, error);
    throw new Error(`Failed to fetch driver: ${error.message}`);
  }
};

/**
 * Create a driver. The `uid` argument MUST be the Firebase Auth UID of the
 * matching Auth user (admin creates the Auth user via Firebase Console
 * first, copies the UID into the Drivers admin page).
 *
 * Uses `setDoc(uid)` rather than `addDoc()` so the document ID matches the
 * UID — required for the Firestore Security Rules to look up
 * `drivers/{request.auth.uid}` on every request.
 *
 * @param {string} uid    Firebase Auth UID of the driver
 * @param {Object} driver Driver data (name, email, phone, etc.)
 * @returns {Promise<Object>} Created driver with id = uid
 * @throws if a driver doc already exists at that UID
 */
const createDriver = async (uid, driver) => {
  try {
    if (!uid || typeof uid !== "string") {
      throw new Error("Driver UID is required and must be a string");
    }

    const validation = validateDriverData(driver, false);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.error}`);
    }

    // Refuse to overwrite an existing doc — that would be either a UID
    // collision or accidental double-create. Caller can deleteDriver
    // first if they really want to replace.
    const existingRef = doc(db, DRIVERS_COLLECTION, uid);
    const existingSnap = await getDoc(existingRef);
    if (existingSnap.exists()) {
      throw new Error(
        `A driver with UID ${uid} already exists. Edit the existing record instead.`
      );
    }

    const sanitised = sanitizeDriverData({ disabled: false, ...driver });
    sanitised.createdAt = Timestamp.now();

    await setDoc(existingRef, sanitised);

    return { id: uid, ...sanitised };
  } catch (error) {
    console.error("❌ Error creating driver:", error);
    throw new Error(`Failed to create driver: ${error.message}`);
  }
};

/**
 * Update a driver. Partial updates allowed.
 */
const updateDriver = async (uid, driver) => {
  try {
    if (!uid || typeof uid !== "string") {
      throw new Error("Driver UID is required and must be a string");
    }

    const validation = validateDriverData(driver, true);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.error}`);
    }

    const sanitised = sanitizeDriverData(driver);
    const docRef = doc(db, DRIVERS_COLLECTION, uid);
    await updateDoc(docRef, sanitised);

    return { id: uid, ...sanitised };
  } catch (error) {
    console.error(`❌ Error updating driver ${uid}:`, error);
    throw new Error(`Failed to update driver: ${error.message}`);
  }
};

/**
 * Soft-disable a driver. Sets `disabled: true`. Does NOT touch the
 * Firebase Auth user — admin must separately disable the Auth user in
 * the Firebase Console for full lockout. The Drivers admin page should
 * remind the user of this when toggling.
 */
const setDriverDisabled = async (uid, disabled) => {
  return updateDriver(uid, { disabled: !!disabled });
};

/**
 * Hard-delete a driver document. The Firebase Auth user is NOT touched —
 * admin must separately delete or disable it in the Firebase Console.
 *
 * Caller should check for outstanding calendar assignments first (events
 * where `driverId === uid`) and either unassign or reassign them before
 * calling this — `getCalendarsForDriver` in `calendarService.js` (added
 * in Sprint 2) is the lookup.
 */
const deleteDriver = async (uid) => {
  try {
    if (!uid || typeof uid !== "string") {
      throw new Error("Driver UID is required and must be a string");
    }

    const docRef = doc(db, DRIVERS_COLLECTION, uid);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return false;

    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error(`❌ Error deleting driver ${uid}:`, error);
    throw new Error(`Failed to delete driver: ${error.message}`);
  }
};

// ==========================================
// EXPORTS
// ==========================================

export {
  getDrivers,
  getDriverByUid,
  createDriver,
  updateDriver,
  setDriverDisabled,
  deleteDriver,

  // utilities — exposed for tests / advanced callers
  validateDriverData,
  sanitizeDriverData,
};
